import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatStatistic } from "@/lib/Formatters/formatStatistic";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { adminDashboardStatsQuery } from "@/app/Dashboard/GraphQL/adminDashboardStatsQuery";
import { toDateTimeTzString } from "@/lib/Formatters/toDateTimeString";
import { TrendIndicator } from "@/app/Dashboard/Components/Blocks/TrendIndicator";
import { useTranslations } from "use-intl";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import colors from "tailwindcss/colors";
import { StatBlock } from "@/app/Dashboard/Components/Blocks/types";
import { Region } from "@/gql/graphql";

export function RegionalBookingStatBlock({
  start,
  end,
  comparisonMode,
}: StatBlock) {
  const t = useTranslations();
  const { data: adminData } = useSuspenseGqlQuery(adminDashboardStatsQuery, {
    start: toDateTimeTzString(start),
    end: toDateTimeTzString(end),
    comparisonMode,
  });

  return (
    <Card className="flex flex-col">
      <CardHeader className="space-y-0">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <CardTitle className="text-base font-medium">
              {t("dashboard.blocks.bookings.conversionsTitle")}
            </CardTitle>
            <div className="mt-4 flex flex-wrap gap-2">
              {adminData.adminDashboardStats.android_bookings_count > 0 ? (
                <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                  {t("dashboard.blocks.bookings.platforms.android")}:{" "}
                  {adminData.adminDashboardStats.android_bookings_count}
                </span>
              ) : null}
              {adminData.adminDashboardStats.ios_bookings_count > 0 ? (
                <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                  {t("dashboard.blocks.bookings.platforms.ios")}:{" "}
                  {adminData.adminDashboardStats.ios_bookings_count}
                </span>
              ) : null}
              {adminData.adminDashboardStats.web_bookings_count > 0 ? (
                <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                  {t("dashboard.blocks.bookings.platforms.web")}:{" "}
                  {adminData.adminDashboardStats.web_bookings_count}
                </span>
              ) : null}
              {adminData.adminDashboardStats.unknown_platform_bookings_count >
              0 ? (
                <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
                  {t("dashboard.blocks.bookings.platforms.unknown")}:{" "}
                  {
                    adminData.adminDashboardStats
                      .unknown_platform_bookings_count
                  }
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
            <div className="text-3xl font-bold">
              {formatStatistic(adminData.adminDashboardStats.bookings_count)}
            </div>
            <TrendIndicator
              trend={adminData.adminDashboardStats.bookings_trend}
              currentValue={adminData.adminDashboardStats.bookings_count}
              startDate={start}
              endDate={end}
              comparisonMode={comparisonMode}
              formatValue={formatStatistic}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="mt-auto min-w-0">
        <BookingRegionStackedBarChart
          data={adminData.adminDashboardStats.regions}
        />
      </CardContent>
    </Card>
  );
}

function BookingRegionStackedBarChart({
  data,
}: {
  data: Array<{
    region: string;
    bookings_count: number;
    bookings_pending_count: number;
    bookings_cancelled_count: number;
  }>;
}) {
  const tCity = useTranslations("city");
  const tDashboard = useTranslations("dashboard");
  const filteredData = data.filter(
    (item) => item.region !== Region.Pi && item.region !== Region.Sacu,
  );

  const chartConfig = {
    bookings_cancelled_count: {
      label: tDashboard("chartLabels.cancelled"),
      color: colors["red"]["500"],
    },
    bookings_pending_count: {
      label: tDashboard("chartLabels.pendingAndVip"),
      color: colors["yellow"]["500"],
    },
    bookings_count: {
      label: tDashboard("chartLabels.confirmedAndCompleted"),
      color: colors["green"]["500"],
    },
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} className="h-[200px] w-full">
      <BarChart data={filteredData}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="region"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          fontSize={12}
        />
        <YAxis tickLine={false} axisLine={false} fontSize={12} />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(value) => tCity(`region.${value}`)}
            />
          }
        />
        <Bar
          dataKey="bookings_count"
          stackId="a"
          fill="var(--color-bookings_count)"
          radius={[0, 0, 0, 0]}
        />
        <Bar
          dataKey="bookings_pending_count"
          stackId="a"
          fill="var(--color-bookings_pending_count)"
          radius={[0, 0, 0, 0]}
        />
        <Bar
          dataKey="bookings_cancelled_count"
          stackId="a"
          fill="var(--color-bookings_cancelled_count)"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ChartContainer>
  );
}
